import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  Paper,
  Skeleton,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { Close } from '@mui/icons-material'
import { useForm } from '@tanstack/react-form'
import { PreviewIcon, ShowNotesIcon, VideoIcon } from 'src/v3/core/icons'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchPlanningCoursesV2,
  fetchPlanningCourseV2Details,
  fetchCourseV2Compliances,
  fetchPlanningCoursesV2Progress,
  fetchPlanningSections,
  fetchPlanningTemplates,
  selectPlanningCoursesV2,
  selectPlanningCoursesV2Status,
  selectPlanningTemplates,
  selectPlanningCourseV2DetailsByCourseID,
  selectPlanningCourseV2DetailsStatusByCourseID,
  selectCourseV2ComplianceByCourseID,
  selectCourseV2ComplianceStatus,
  selectCourseV2Progress,
  selectCourseV2ProgressStatus,
  selectPlanningSections,
  selectPlanningSectionsStatus,
  selectPlanningTemplatesStatus,
  fetchCourseV2CAPCEAccreditation,
  fetchCourseV2ANCCAccreditation,
  fetchCourseV2QuizThunk,
  fetchCourseV2QuizQuestionsThunk,
  selectCourseV2CAPCEAccreditationByCourseID,
  selectCourseV2CAPCEAccreditationStatusByCourseID,
  selectCourseV2ANCCAccreditationByCourseID,
  selectCourseV2ANCCAccreditationStatusByCourseID,
  selectCourseV2QuizByCourseID,
  selectCourseV2QuizStatusByCourseID,
  selectCourseV2QuizQuestionsByCourseID,
  selectCourseV2QuizQuestionsStatusByCourseID,
  fetchCourseV2ShowNotes,
  fetchCourseV2Videos,
  selectCourseV2ShowNotesByCourseID,
  selectCourseV2ShowNotesStatusByCourseID,
  selectCourseV2VideosByCourseID,
  selectCourseV2VideosStatusByCourseID,
  preArchiveCourse,
  reactivateCourse,
  archiveCourse,
  restoreCourse,
  deletePlanningCourseV2,
  clearCourseLifecycleOperationStatus,
  selectCourseLifecycleOperationStatus,
  selectCourseLifecycleOperationError,
  selectCourseLifecycleOperationSuccess,
} from 'src/v3/content-pipeline/course-pipeline.slice'
import { selectRoles } from 'src/v3/settings/settings.slice'
import ConfirmAction from 'src/v3/components/confirm-action'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import { ContentComplianceStatus } from 'src/v3/core/types'
import { createCourseV2ComplianceEvent } from 'src/v3/content-pipeline/utils/course-api'
import { modalPaperProps } from 'src/v3/utilities/modal-styles'
import { getS3ImageFilePath } from 'src/v3/utilities/image-upload'
import {
  CourseContentItem,
  orderCourseContent,
} from 'src/v3/content-pipeline/utils/order-course-content'
import CourseField from '../planning/course/fields/course-field'
import VideoPreviewModal from '../planning/course/components/video-preview-modal'
import CourseShowNotesPreviewModal from '../planning/course/components/course-show-notes-preview-modal'
import CourseListWithProgress from '../planning/course/components/course-list-with-progress'
import CourseInstructorsManagement from '../planning/course/components/course-instructors-management'
import SelectItemHint from '../planning/select-item-hint'
import LifecycleChip from '../components/lifecycle/lifecycle-chip'
import {
  LifecycleFilterValue,
  filterByLifecycle,
  lifecycleCounts,
} from '../components/lifecycle/lifecycle-filter'
import PipelineFilterBar from '../components/pipeline-filter-bar'
import { CAPCEApproval, ANCCApproval, QuizApproval } from './field-approval'

type FieldFilter = 'all' | 'pending' | 'approved'

let capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1)

let colors: Record<ContentComplianceStatus, any> = {
  new: 'primary',
  approved: 'success',
  rejected: 'error',
  updated: 'primary',
  unapproved: 'error',
}

let FieldApproval = ({
  courseID,
  fieldName,
  fieldLabel,
  status,
  children,
  filter,
  onEdit,
}: {
  courseID: string
  fieldName:
    | 'Name'
    | 'Description'
    | 'Hours'
    | 'Thumbnail'
    | 'Banner'
    | 'Template'
    | 'Section'
    | 'PracticeGap'
    | 'PracticeGapEvidence'
    | 'LearnerEngagement'
    | 'LearningOutcomes'
    | 'Objectives'
    | 'References'
    | 'Instructors'
    | 'SyllabusDescription'
    | 'Content'
    | 'Capce'
    | 'Ancc'
    | 'Quiz'
  fieldLabel: string
  status: ContentComplianceStatus
  children: ReactElement
  filter: FieldFilter
  onEdit?: () => void
}): ReactElement => {
  let dispatch = useAppDispatch()
  let [loading, setLoading] = useState<'approve' | 'reject'>()

  let remove =
    (filter === 'approved' && status !== 'approved') ||
    (filter === 'pending' && status === 'approved')

  let form = useForm({
    defaultValues: {
      feedback: '',
    },
    onSubmitMeta: {} as { status?: ContentComplianceStatus },
    onSubmit: async ({ value: vals, meta }) => {
      setLoading(meta.status === 'approved' ? 'approve' : 'reject')
      try {
        await createCourseV2ComplianceEvent({
          courseID,
          field: fieldName,
          status: capitalize(meta.status || '') as ContentComplianceStatus,
          complianceFeedback: vals.feedback || null,
        })
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(fetchCourseV2Compliances())
        form.reset()
      } finally {
        setLoading(undefined)
      }
    },
  })

  useEffect(() => {
    form.reset()
  }, [courseID])

  return remove ? (
    <></>
  ) : (
    <Card
      variant="outlined"
      sx={{
        p: 2,
        bgcolor: 'background.paper',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 'medium',
            }}
          >
            {fieldLabel}
          </Typography>
          {onEdit && (
            <Button
              size="small"
              variant="outlined"
              onClick={onEdit}
              sx={{ minWidth: 'auto', px: 1, py: 0.25 }}
              aria-label={`Edit ${fieldLabel} field`}
            >
              Edit
            </Button>
          )}
        </Box>
        <Chip
          size="small"
          label={status === 'new' ? 'Pending Review' : capitalize(status)}
          variant="outlined"
          color={colors[status]}
        />
      </Box>
      <Divider sx={{ my: 1.5 }} />
      <Box
        sx={{
          px: 2,
          py: 1.25,
          bgcolor: 'background.paperDark',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        {children}
      </Box>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
      >
        <form.Field
          name="feedback"
          children={(field) => (
            <TextField
              sx={{ mt: 1.5, mb: 1.25, bgcolor: 'background.paperDark' }}
              fullWidth
              size="small"
              multiline
              label="Feedback"
              name={field.name}
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              onBlur={field.handleBlur}
            />
          )}
        />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          {status !== 'approved' && (
            <Button
              color="success"
              type="submit"
              variant="contained"
              onClick={() => form.handleSubmit({ status: 'approved' })}
              disabled={loading === 'approve'}
            >
              {loading === 'approve' ? 'Approving...' : 'Approve'}
            </Button>
          )}
          {status === 'approved' && (
            <Button
              color="error"
              type="submit"
              variant="outlined"
              onClick={() => form.handleSubmit({ status: 'unapproved' })}
              disabled={loading === 'reject'}
            >
              {loading === 'reject' ? 'Unapproving...' : 'Unapprove'}
            </Button>
          )}
          {status !== 'rejected' && status !== 'approved' && (
            <Button
              color="error"
              type="submit"
              variant="outlined"
              onClick={() => form.handleSubmit({ status: 'rejected' })}
              disabled={loading === 'reject'}
            >
              {loading === 'reject' ? 'Rejecting...' : 'Reject'}
            </Button>
          )}
        </Box>
      </form>
    </Card>
  )
}

export default function CourseV2ComplianceDashboard(): ReactElement {
  let dispatch = useAppDispatch()
  let theme = useTheme()

  let courses = useAppSelector(selectPlanningCoursesV2)
  let coursesStatus = useAppSelector(selectPlanningCoursesV2Status)
  let coursesProgress = useAppSelector(selectCourseV2Progress)
  let coursesProgressStatus = useAppSelector(selectCourseV2ProgressStatus)
  let courseComplianceStatus = useAppSelector(selectCourseV2ComplianceStatus)
  let sections = useAppSelector(selectPlanningSections)
  let sectionsStatus = useAppSelector(selectPlanningSectionsStatus)
  let templatesStatus = useAppSelector(selectPlanningTemplatesStatus)
  let templates = useAppSelector(selectPlanningTemplates)
  let roles = useAppSelector(selectRoles)
  let canTransitionLifecycle = roles.includes('content-compliance')
  let lifecycleOperationStatus = useAppSelector(
    selectCourseLifecycleOperationStatus,
  )
  let lifecycleOperationError = useAppSelector(
    selectCourseLifecycleOperationError,
  )
  let lifecycleOperationSuccess = useAppSelector(
    selectCourseLifecycleOperationSuccess,
  )

  let [activeCourseID, setActiveCourseID] = useState<string>()
  let [courseFilter, setCourseFilter] = useState<
    'all' | 'needs-revision' | 'in-planning' | 'ready-for-review' | 'approved'
  >('in-planning')
  let [lifecycleFilter, setLifecycleFilter] =
    useState<LifecycleFilterValue>('all')
  let [lifecycleConfirm, setLifecycleConfirm] = useState<string>('')
  let [searchQuery, setSearchQuery] = useState('')
  let [detailsFilter, setDetailsFilter] = useState<FieldFilter>('pending')
  let [activeTab, setActiveTab] = useState(0)
  let [editModalOpen, setEditModalOpen] = useState(false)
  let [instructorModalOpen, setInstructorModalOpen] = useState(false)
  let [editModalField, setEditModalField] = useState<{
    fieldName:
      | 'name'
      | 'description'
      | 'length'
      | 'sectionID'
      | 'thumbnail'
      | 'banner'
      | 'templateID'
      | 'practiceGap'
      | 'practiceGapEvidence'
      | 'learnerEngagement'
      | 'learningOutcomes'
    fieldLabel: string
    fieldType: 'text' | 'textarea' | 'number' | 'select' | 'image'
  }>()

  let activeCourseDetails = useAppSelector(
    selectPlanningCourseV2DetailsByCourseID(activeCourseID || ''),
  )
  let activeCourseDetailsStatus = useAppSelector(
    selectPlanningCourseV2DetailsStatusByCourseID(activeCourseID || ''),
  )
  let activeCourseCompliance = useAppSelector(
    selectCourseV2ComplianceByCourseID(activeCourseID || ''),
  )
  let activeCourseCapce = useAppSelector(
    selectCourseV2CAPCEAccreditationByCourseID(activeCourseID || ''),
  )
  let activeCourseCapceStatus = useAppSelector(
    selectCourseV2CAPCEAccreditationStatusByCourseID(activeCourseID || ''),
  )
  let activeCourseAncc = useAppSelector(
    selectCourseV2ANCCAccreditationByCourseID(activeCourseID || ''),
  )
  let activeCourseAnccStatus = useAppSelector(
    selectCourseV2ANCCAccreditationStatusByCourseID(activeCourseID || ''),
  )
  let activeCourseQuiz = useAppSelector(
    selectCourseV2QuizByCourseID(activeCourseID || ''),
  )
  let activeCourseQuizStatus = useAppSelector(
    selectCourseV2QuizStatusByCourseID(activeCourseID || ''),
  )
  let activeCourseQuizQuestions = useAppSelector(
    selectCourseV2QuizQuestionsByCourseID(activeCourseID || ''),
  )
  let activeCourseQuizQuestionsStatus = useAppSelector(
    selectCourseV2QuizQuestionsStatusByCourseID(activeCourseID || ''),
  )
  let activeCourseShowNotes = useAppSelector(
    selectCourseV2ShowNotesByCourseID(activeCourseID || ''),
  )
  let activeCourseShowNotesStatus = useAppSelector(
    selectCourseV2ShowNotesStatusByCourseID(activeCourseID || ''),
  )
  let activeCourseVideos = useAppSelector(
    selectCourseV2VideosByCourseID(activeCourseID || ''),
  )
  let activeCourseVideosStatus = useAppSelector(
    selectCourseV2VideosStatusByCourseID(activeCourseID || ''),
  )
  let orderedCourseContent = useMemo(
    () =>
      orderCourseContent(
        activeCourseDetails?.courseSteps ?? [],
        activeCourseShowNotes,
        activeCourseVideos,
      ).items,
    [
      activeCourseDetails?.courseSteps,
      activeCourseShowNotes,
      activeCourseVideos,
    ],
  )
  let [previewContentItem, setPreviewContentItem] =
    useState<CourseContentItem | null>(null)

  // Auto-fetch data
  useAutoFetch(coursesStatus, fetchPlanningCoursesV2)
  useAutoFetch(courseComplianceStatus, fetchCourseV2Compliances)
  useAutoFetch(coursesProgressStatus, fetchPlanningCoursesV2Progress)
  useAutoFetch(sectionsStatus, fetchPlanningSections)
  useAutoFetch(templatesStatus, fetchPlanningTemplates)

  // Calculate workflow status for a course
  let getCourseWorkflowStatus = useMemo(
    () =>
      (
        course: (typeof courses)[number],
      ): 'needs-revision' | 'in-planning' | 'ready-for-review' | 'approved' => {
        let progress = coursesProgress[course.id]
        if (!progress) return 'in-planning'

        let dataComplete =
          (progress?.dataCompleteness?.percentComplete ?? 0) === 100
        let complianceComplete =
          (progress?.complianceStatus?.percentApproved ?? 0) === 100
        let hasRejected = (progress?.complianceStatus?.rejected ?? 0) > 0

        if (hasRejected) return 'needs-revision'
        if (!dataComplete) return 'in-planning'
        if (!complianceComplete) return 'ready-for-review'
        return 'approved'
      },
    [coursesProgress],
  )

  // Calculate overview statistics
  let overviewStats = useMemo(
    () =>
      courses.reduce(
        (stats, course) => {
          let status = getCourseWorkflowStatus(course)
          let progress = coursesProgress[course.id]
          let missingFieldsCount =
            progress?.dataCompleteness?.missingFields?.length ?? 0

          return {
            needsRevision:
              stats.needsRevision + (status === 'needs-revision' ? 1 : 0),
            inPlanning: stats.inPlanning + (status === 'in-planning' ? 1 : 0),
            readyForReview:
              stats.readyForReview + (status === 'ready-for-review' ? 1 : 0),
            approved: stats.approved + (status === 'approved' ? 1 : 0),
            totalFieldsRemaining:
              stats.totalFieldsRemaining + missingFieldsCount,
          }
        },
        {
          needsRevision: 0,
          inPlanning: 0,
          readyForReview: 0,
          approved: 0,
          totalFieldsRemaining: 0,
        },
      ),
    [courses, coursesProgress, getCourseWorkflowStatus],
  )

  // Group and filter courses
  let filteredCourses = useMemo(() => {
    let lifecycleFiltered = filterByLifecycle(courses, lifecycleFilter)
    let statusFiltered =
      courseFilter === 'all'
        ? lifecycleFiltered
        : lifecycleFiltered.filter(
            (course) => getCourseWorkflowStatus(course) === courseFilter,
          )
    if (!searchQuery) return statusFiltered
    let query = searchQuery.toLowerCase()
    return statusFiltered.filter((course) =>
      course.name.toLowerCase().includes(query),
    )
  }, [
    courses,
    courseFilter,
    lifecycleFilter,
    searchQuery,
    getCourseWorkflowStatus,
  ])

  // Fetch details when active course changes
  useEffect(() => {
    if (
      activeCourseID &&
      (!activeCourseDetailsStatus ||
        activeCourseDetailsStatus === 'uninitialized')
    ) {
      dispatch(fetchPlanningCourseV2Details(activeCourseID))
    }
  }, [activeCourseID, activeCourseDetailsStatus, dispatch])

  // Fetch CAPCE/ANCC when accreditation tab is active
  useEffect(() => {
    if (activeCourseID && activeTab === 2) {
      if (
        !activeCourseCapceStatus ||
        activeCourseCapceStatus === 'uninitialized'
      ) {
        dispatch(fetchCourseV2CAPCEAccreditation(activeCourseID))
      }
      if (
        !activeCourseAnccStatus ||
        activeCourseAnccStatus === 'uninitialized'
      ) {
        dispatch(fetchCourseV2ANCCAccreditation(activeCourseID))
      }
    }
  }, [
    activeCourseID,
    activeTab,
    activeCourseCapceStatus,
    activeCourseAnccStatus,
    dispatch,
  ])

  // Fetch quiz when assessment tab is active
  useEffect(() => {
    if (activeCourseID && activeTab === 3) {
      if (
        !activeCourseQuizStatus ||
        activeCourseQuizStatus === 'uninitialized'
      ) {
        dispatch(fetchCourseV2QuizThunk(activeCourseID))
      }
    }
  }, [activeCourseID, activeTab, activeCourseQuizStatus, dispatch])

  // Fetch quiz questions once quiz is loaded
  useEffect(() => {
    if (activeCourseID && activeCourseQuiz && activeTab === 3) {
      if (
        !activeCourseQuizQuestionsStatus ||
        activeCourseQuizQuestionsStatus === 'uninitialized'
      ) {
        dispatch(fetchCourseV2QuizQuestionsThunk(activeCourseID))
      }
    }
  }, [
    activeCourseID,
    activeCourseQuiz,
    activeTab,
    activeCourseQuizQuestionsStatus,
    dispatch,
  ])

  // Fetch content (show notes + videos) when content tab is active
  useEffect(() => {
    if (activeCourseID && activeTab === 4) {
      if (
        !activeCourseShowNotesStatus ||
        activeCourseShowNotesStatus === 'uninitialized'
      ) {
        dispatch(fetchCourseV2ShowNotes(activeCourseID))
      }
      if (
        !activeCourseVideosStatus ||
        activeCourseVideosStatus === 'uninitialized'
      ) {
        dispatch(fetchCourseV2Videos(activeCourseID))
      }
    }
  }, [
    activeCourseID,
    activeTab,
    activeCourseShowNotesStatus,
    activeCourseVideosStatus,
    dispatch,
  ])

  // Set first course as active when courses load
  useEffect(() => {
    if (filteredCourses.length > 0 && !activeCourseID) {
      setActiveCourseID(filteredCourses[0].id)
    }
  }, [filteredCourses, activeCourseID])

  // Set default filter based on what needs attention
  useEffect(() => {
    if (
      coursesProgressStatus === 'fulfilled' &&
      courseFilter === 'in-planning'
    ) {
      // If no courses in planning, switch to "ready-for-review"
      if (overviewStats.inPlanning === 0 && overviewStats.readyForReview > 0) {
        setCourseFilter('ready-for-review')
      }
    }
  }, [
    coursesProgressStatus,
    overviewStats.inPlanning,
    overviewStats.readyForReview,
    courseFilter,
  ])

  // Reset active course if it's filtered out
  useEffect(() => {
    if (
      activeCourseID &&
      !filteredCourses.some((course) => course.id === activeCourseID)
    ) {
      setActiveCourseID(undefined)
    }
  }, [
    courseFilter,
    lifecycleFilter,
    searchQuery,
    filteredCourses,
    activeCourseID,
  ])

  let isLoading = coursesStatus === 'pending'
  let isDetailsLoading = activeCourseDetailsStatus === 'pending'

  let activeCourseData = courses.find((course) => course.id === activeCourseID)
  let activeCourseProgress = activeCourseID
    ? coursesProgress[activeCourseID]
    : undefined
  let activeCourseTemplateName =
    templates.find(
      (template) => template.id === activeCourseDetails?.templateID,
    )?.name ?? activeCourseDetails?.templateID

  let openEditModal = (
    fieldName:
      | 'name'
      | 'description'
      | 'length'
      | 'sectionID'
      | 'thumbnail'
      | 'banner'
      | 'templateID'
      | 'practiceGap'
      | 'practiceGapEvidence'
      | 'learnerEngagement'
      | 'learningOutcomes',
    fieldLabel: string,
    fieldType: 'text' | 'textarea' | 'number' | 'select' | 'image',
  ) => {
    setEditModalField({ fieldName, fieldLabel, fieldType })
    setEditModalOpen(true)
  }

  let closeEditModal = () => {
    setEditModalOpen(false)
    setEditModalField(undefined)
  }

  return (
    <Box>
      <Typography variant="h5" component="h2" sx={{ mb: 1 }}>
        Enduring Courses (F3) - Content Compliance Review Dashboard
      </Typography>
      <Typography
        color="text.secondary"
        variant="body2"
        sx={{
          mb: 3,
        }}
      >
        Review and approve enduring content courses for compliance with
        accreditation standards. Enduring content are self-paced courses that
        students can take at any time. Verify course details, templates, and
        content to ensure quality and regulatory compliance before publication.
      </Typography>
      <Card
        variant="outlined"
        sx={{ mb: 3, p: 3 }}
        component="aside"
        aria-label="Overview statistics"
      >
        <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
          Overview
        </Typography>
        <Divider sx={{ my: 1 }} />
        {coursesProgressStatus === 'pending' ||
        coursesProgressStatus === 'uninitialized' ? (
          <Box sx={{ display: 'flex', gap: 4, mt: 2 }}>
            {[...Array(5)].map((_, i) => (
              <Box key={i}>
                <Skeleton
                  variant="text"
                  sx={{
                    width: 40,
                    height: 30,
                  }}
                />
                <Skeleton
                  variant="text"
                  sx={{
                    width: 100,
                    height: 20,
                  }}
                />
              </Box>
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              columnGap: 4,
              rowGap: 1,
              flexWrap: 'wrap',
              mt: 2,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  fontSize: 20,
                  color: theme.palette.error.main,
                }}
              >
                {overviewStats.needsRevision}
              </Typography>
              <Typography variant="subtitle1">Needs Revision</Typography>
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  fontSize: 20,
                  color: theme.palette.contentPipelineColors.planning,
                }}
              >
                {overviewStats.inPlanning}
              </Typography>
              <Typography variant="subtitle1">In Planning</Typography>
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  fontSize: 20,
                  color: theme.palette.contentPipelineColors.compliance,
                }}
              >
                {overviewStats.readyForReview}
              </Typography>
              <Typography variant="subtitle1">Ready for Review</Typography>
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  fontSize: 20,
                  color: theme.palette.success.main,
                }}
              >
                {overviewStats.approved}
              </Typography>
              <Typography variant="subtitle1">Approved</Typography>
            </Box>
            <Box>
              <Typography
                sx={{
                  fontWeight: 'bold',
                  fontSize: 20,
                  color: theme.palette.contentPipelineColors.planning,
                }}
              >
                {overviewStats.totalFieldsRemaining}
              </Typography>
              <Typography variant="subtitle1">Fields Remaining</Typography>
            </Box>
          </Box>
        )}
      </Card>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h5" component="h2" sx={{ mb: 1.5 }}>
          Courses for Review
        </Typography>
        <PipelineFilterBar
          search={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search courses…"
          status={courseFilter}
          onStatusChange={setCourseFilter}
          statusCounts={{
            needsRevision: overviewStats.needsRevision,
            inPlanning: overviewStats.inPlanning,
            readyForReview: overviewStats.readyForReview,
            approved: overviewStats.approved,
            all: courses?.length ?? 0,
          }}
          lifecycle={lifecycleFilter}
          onLifecycleChange={setLifecycleFilter}
          lifecycleCounts={lifecycleCounts(courses)}
        />
      </Paper>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { sm: '1fr', md: '350px 1fr' },
          gap: 3,
          alignItems: 'start',
        }}
      >
        <Box
          sx={{
            position: { md: 'sticky' },
            top: { md: 16 },
            maxHeight: { md: 'calc(100vh - 32px)' },
            overflowY: { md: 'auto' },
          }}
        >
          <Paper
            sx={{ p: 3 }}
            component="nav"
            aria-label="Compliance courses list"
          >
            <CourseListWithProgress
              courses={filteredCourses}
              coursesProgress={coursesProgress}
              activeCourseID={activeCourseID}
              onSelectCourse={setActiveCourseID}
              isLoading={isLoading}
              filterType={courseFilter}
              searchQuery={searchQuery}
            />
          </Paper>
        </Box>

        <Box component="main" role="main" aria-label="Course compliance review">
          {!activeCourseID ? (
            <SelectItemHint type="course" />
          ) : (
            <>
              <Card variant="outlined" sx={{ p: 3, mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: 'wrap',
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography variant="h5" component="h2">
                      {activeCourseData
                        ? `Compliance Review: ${activeCourseData.name}`
                        : 'Compliance Review'}
                    </Typography>
                    <LifecycleChip
                      lifecycleState={activeCourseData?.lifecycleState}
                    />
                  </Box>
                  {canTransitionLifecycle && activeCourseData && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {(!activeCourseData.lifecycleState ||
                        activeCourseData.lifecycleState === 'active') && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            setLifecycleConfirm('lifecycle-pre-archive')
                          }
                        >
                          Move to pre-archive
                        </Button>
                      )}
                      {activeCourseData.lifecycleState === 'preArchive' && (
                        <>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              setLifecycleConfirm('lifecycle-reactivate')
                            }
                          >
                            Reactivate
                          </Button>
                          <Button
                            color="warning"
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              setLifecycleConfirm('lifecycle-archive')
                            }
                          >
                            Archive
                          </Button>
                        </>
                      )}
                      {activeCourseData.lifecycleState === 'archive' && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            setLifecycleConfirm('lifecycle-restore')
                          }
                        >
                          Restore
                        </Button>
                      )}
                      <Button
                        color="error"
                        size="small"
                        variant="outlined"
                        onClick={() => setLifecycleConfirm('delete-course')}
                      >
                        Delete
                      </Button>
                    </Box>
                  )}
                </Box>
                {activeCourseData && (
                  <Box
                    sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}
                  >
                    <Chip
                      label={
                        activeCourseData.published === 'published'
                          ? 'Published'
                          : 'Unpublished'
                      }
                      size="small"
                      variant="outlined"
                      color={
                        activeCourseData.published === 'published'
                          ? 'success'
                          : undefined
                      }
                    />
                  </Box>
                )}
                {activeCourseProgress && (
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}
                  >
                    <Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 'medium' }}
                        >
                          Data Entry
                        </Typography>
                        <Typography color="text.secondary" variant="body2">
                          {Math.round(
                            activeCourseProgress.dataCompleteness
                              .percentComplete ?? 0,
                          )}
                          %
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={
                          activeCourseProgress.dataCompleteness
                            .percentComplete ?? 0
                        }
                        sx={{
                          height: 6,
                          borderRadius: 2,
                          bgcolor: 'rgba(255,255,255,0.1)',
                          '& .MuiLinearProgress-bar': {
                            bgcolor:
                              theme.palette.contentPipelineColors.planning,
                          },
                        }}
                      />
                    </Box>
                    <Box>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          mb: 0.5,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 'medium' }}
                        >
                          Compliance
                        </Typography>
                        <Typography color="text.secondary" variant="body2">
                          {Math.round(
                            activeCourseProgress.complianceStatus
                              .percentApproved ?? 0,
                          )}
                          %
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={
                          activeCourseProgress.complianceStatus
                            .percentApproved ?? 0
                        }
                        sx={{
                          height: 6,
                          borderRadius: 2,
                          bgcolor: 'rgba(255,255,255,0.1)',
                          '& .MuiLinearProgress-bar': {
                            bgcolor:
                              theme.palette.contentPipelineColors.compliance,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                )}
              </Card>
              {activeCourseID && activeCourseData && (
                <>
                  <ToggleButtonGroup
                    color="primary"
                    sx={{
                      mb: 3,
                      bgcolor: 'background.paperDark',
                      borderRadius: 1,
                      p: 0.5,
                      '& .MuiToggleButton-root': {
                        border: 0,
                        borderRadius: 1,
                        textTransform: 'none',
                        px: 2,
                      },
                      '& .Mui-selected': {
                        bgcolor: 'action.selected',
                      },
                    }}
                    size="small"
                    value={detailsFilter}
                    exclusive
                    onChange={(_, value) => value && setDetailsFilter(value)}
                    aria-label="Filter fields by compliance status"
                  >
                    <ToggleButton value="all">All</ToggleButton>
                    <ToggleButton value="pending">Pending</ToggleButton>
                    <ToggleButton value="approved">Approved</ToggleButton>
                  </ToggleButtonGroup>
                  <Tabs
                    value={activeTab}
                    onChange={(_, newValue) => setActiveTab(newValue)}
                    variant="scrollable"
                    scrollButtons
                    allowScrollButtonsMobile
                    sx={{
                      mb: 3,
                      borderBottom: 1,
                      borderColor: 'divider',
                      minHeight: 40,
                      '& .MuiTab-root': {
                        minHeight: 40,
                        textTransform: 'none',
                      },
                    }}
                    aria-label="Course compliance sections"
                  >
                    <Tab
                      label="Course Details"
                      value={0}
                      id="tab-0"
                      aria-controls="tabpanel-0"
                    />
                    <Tab
                      label="Accreditation & Setup"
                      value={2}
                      id="tab-2"
                      aria-controls="tabpanel-2"
                    />
                    <Tab
                      label="Syllabus"
                      value={1}
                      id="tab-1"
                      aria-controls="tabpanel-1"
                    />
                    <Tab
                      label="Content"
                      value={4}
                      id="tab-4"
                      aria-controls="tabpanel-4"
                    />
                    <Tab
                      label="Assessment"
                      value={3}
                      id="tab-3"
                      aria-controls="tabpanel-3"
                    />
                  </Tabs>
                </>
              )}

              {isDetailsLoading ? (
                <Box sx={{ p: 4 }}>
                  {[...Array(5)].map((_, i) => (
                    <Skeleton
                      key={i}
                      variant="rectangular"
                      sx={{
                        height: 120,
                        mb: 2,
                        borderRadius: 1,
                      }}
                    />
                  ))}
                </Box>
              ) : activeCourseDetails ? (
                <>
                  <Box
                    role="tabpanel"
                    id="tabpanel-0"
                    aria-labelledby="tab-0"
                    hidden={activeTab !== 0}
                  >
                    {activeTab === 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                        }}
                      >
                        <FieldApproval
                          courseID={activeCourseID}
                          fieldName="Name"
                          fieldLabel="Course Name"
                          status={activeCourseCompliance?.name || 'new'}
                          filter={detailsFilter}
                          onEdit={() =>
                            openEditModal('name', 'Course Name', 'text')
                          }
                        >
                          <Typography>{activeCourseDetails.name}</Typography>
                        </FieldApproval>

                        <FieldApproval
                          courseID={activeCourseID}
                          fieldName="Hours"
                          fieldLabel="CE Hours"
                          status={activeCourseCompliance?.hours || 'new'}
                          filter={detailsFilter}
                          onEdit={() =>
                            openEditModal('length', 'CE Hours', 'number')
                          }
                        >
                          <Typography>
                            {activeCourseDetails.length || 'Not set'}
                          </Typography>
                        </FieldApproval>

                        <FieldApproval
                          courseID={activeCourseID}
                          fieldName="Section"
                          fieldLabel="Section (Category)"
                          status={activeCourseCompliance?.section || 'new'}
                          filter={detailsFilter}
                          onEdit={() =>
                            openEditModal(
                              'sectionID',
                              'Section (Category)',
                              'select',
                            )
                          }
                        >
                          <Typography>
                            {sections.find(
                              (s) => s.id === activeCourseDetails.sectionID,
                            )?.name || 'Not set'}
                          </Typography>
                        </FieldApproval>

                        <FieldApproval
                          courseID={activeCourseID}
                          fieldName="Thumbnail"
                          fieldLabel="Thumbnail Image"
                          status={activeCourseCompliance?.thumbnail || 'new'}
                          filter={detailsFilter}
                          onEdit={() =>
                            openEditModal(
                              'thumbnail',
                              'Thumbnail Image',
                              'image',
                            )
                          }
                        >
                          {activeCourseDetails.thumbnail ? (
                            <Box sx={{ p: 2 }}>
                              <img
                                style={{
                                  maxHeight: '120px',
                                  maxWidth: '240px',
                                  aspectRatio: '16 / 9',
                                  borderRadius: '4px',
                                  objectFit: 'cover',
                                }}
                                src={getS3ImageFilePath(
                                  activeCourseDetails.thumbnail,
                                )}
                                alt="Thumbnail"
                              />
                            </Box>
                          ) : (
                            <Typography
                              color="text.secondary"
                              sx={{
                                fontStyle: 'italic',
                              }}
                            >
                              No thumbnail uploaded
                            </Typography>
                          )}
                        </FieldApproval>

                        <FieldApproval
                          courseID={activeCourseID}
                          fieldName="Banner"
                          fieldLabel="Banner Image"
                          status={activeCourseCompliance?.banner || 'new'}
                          filter={detailsFilter}
                          onEdit={() =>
                            openEditModal('banner', 'Banner Image', 'image')
                          }
                        >
                          {activeCourseDetails.banner ? (
                            <Box sx={{ p: 2 }}>
                              <img
                                style={{
                                  maxHeight: '80px',
                                  maxWidth: '320px',
                                  aspectRatio: '4 / 1',
                                  borderRadius: '4px',
                                  objectFit: 'cover',
                                }}
                                src={getS3ImageFilePath(
                                  activeCourseDetails.banner,
                                )}
                                alt="Banner"
                              />
                            </Box>
                          ) : (
                            <Typography
                              color="text.secondary"
                              sx={{
                                fontStyle: 'italic',
                              }}
                            >
                              No banner uploaded
                            </Typography>
                          )}
                        </FieldApproval>
                      </Box>
                    )}
                  </Box>

                  <Box
                    role="tabpanel"
                    id="tabpanel-1"
                    aria-labelledby="tab-1"
                    hidden={activeTab !== 1}
                  >
                    {activeTab === 1 && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                        }}
                      >
                        <FieldApproval
                          courseID={activeCourseID}
                          fieldName="SyllabusDescription"
                          fieldLabel="Description"
                          status={
                            activeCourseCompliance?.syllabusDescription || 'new'
                          }
                          filter={detailsFilter}
                          onEdit={() =>
                            openEditModal(
                              'description',
                              'Description',
                              'textarea',
                            )
                          }
                        >
                          <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                            {activeCourseDetails.description || 'Not set'}
                          </Typography>
                        </FieldApproval>

                        <FieldApproval
                          courseID={activeCourseID}
                          fieldName="PracticeGap"
                          fieldLabel="Practice Gap"
                          status={activeCourseCompliance?.practiceGap || 'new'}
                          filter={detailsFilter}
                          onEdit={() =>
                            openEditModal(
                              'practiceGap',
                              'Practice Gap',
                              'textarea',
                            )
                          }
                        >
                          <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                            {activeCourseDetails.practiceGap || 'Not set'}
                          </Typography>
                        </FieldApproval>

                        <FieldApproval
                          courseID={activeCourseID}
                          fieldName="PracticeGapEvidence"
                          fieldLabel="Practice Gap Evidence"
                          status={
                            activeCourseCompliance?.practiceGapEvidence || 'new'
                          }
                          filter={detailsFilter}
                          onEdit={() =>
                            openEditModal(
                              'practiceGapEvidence',
                              'Practice Gap Evidence',
                              'textarea',
                            )
                          }
                        >
                          <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                            {activeCourseDetails.practiceGapEvidence ||
                              'Not set'}
                          </Typography>
                        </FieldApproval>

                        <FieldApproval
                          courseID={activeCourseID}
                          fieldName="LearnerEngagement"
                          fieldLabel="Learner Engagement"
                          status={
                            activeCourseCompliance?.learnerEngagement || 'new'
                          }
                          filter={detailsFilter}
                          onEdit={() =>
                            openEditModal(
                              'learnerEngagement',
                              'Learner Engagement',
                              'textarea',
                            )
                          }
                        >
                          <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                            {activeCourseDetails.learnerEngagement || 'Not set'}
                          </Typography>
                        </FieldApproval>

                        <FieldApproval
                          courseID={activeCourseID}
                          fieldName="LearningOutcomes"
                          fieldLabel="Learning Outcomes"
                          status={
                            activeCourseCompliance?.learningOutcomes || 'new'
                          }
                          filter={detailsFilter}
                          onEdit={() =>
                            openEditModal(
                              'learningOutcomes',
                              'Learning Outcomes',
                              'textarea',
                            )
                          }
                        >
                          <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                            {activeCourseDetails.learningOutcomes || 'Not set'}
                          </Typography>
                        </FieldApproval>

                        <FieldApproval
                          courseID={activeCourseID}
                          fieldName="Objectives"
                          fieldLabel="Objectives"
                          status={activeCourseCompliance?.objectives || 'new'}
                          filter={detailsFilter}
                        >
                          {activeCourseDetails.objectives?.length > 0 ? (
                            <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
                              {activeCourseDetails.objectives.map((obj, i) => (
                                <Typography
                                  component="li"
                                  key={i}
                                  sx={{ mb: 0.5 }}
                                >
                                  {obj.text}
                                </Typography>
                              ))}
                            </Box>
                          ) : (
                            <Typography
                              color="text.secondary"
                              sx={{
                                fontStyle: 'italic',
                              }}
                            >
                              No objectives added
                            </Typography>
                          )}
                        </FieldApproval>

                        <FieldApproval
                          courseID={activeCourseID}
                          fieldName="References"
                          fieldLabel="References"
                          status={activeCourseCompliance?.references || 'new'}
                          filter={detailsFilter}
                        >
                          {activeCourseDetails.references?.length > 0 ? (
                            <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
                              {activeCourseDetails.references.map((ref, i) => (
                                <Typography
                                  component="li"
                                  key={i}
                                  sx={{ mb: 0.5 }}
                                >
                                  {ref.text}
                                </Typography>
                              ))}
                            </Box>
                          ) : (
                            <Typography
                              color="text.secondary"
                              sx={{
                                fontStyle: 'italic',
                              }}
                            >
                              No references added
                            </Typography>
                          )}
                        </FieldApproval>
                      </Box>
                    )}
                  </Box>

                  {/* Tab 2 — Accreditation */}
                  <Box
                    role="tabpanel"
                    id="tabpanel-2"
                    aria-labelledby="tab-2"
                    hidden={activeTab !== 2}
                  >
                    {activeTab === 2 && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                        }}
                      >
                        {/* CAPCE */}
                        {activeCourseCapceStatus === 'pending' ? (
                          <Skeleton
                            variant="rectangular"
                            sx={{ height: 80, borderRadius: 1 }}
                          />
                        ) : activeCourseCapce ? (
                          <FieldApproval
                            courseID={activeCourseID}
                            fieldName="Capce"
                            fieldLabel="CAPCE Accreditation"
                            status={activeCourseCompliance?.capce || 'new'}
                            filter={detailsFilter}
                          >
                            <CAPCEApproval accreditation={activeCourseCapce} />
                          </FieldApproval>
                        ) : (
                          <Box
                            sx={{
                              p: 3,
                              bgcolor: 'background.paperDark',
                              borderRadius: 1,
                              textAlign: 'center',
                            }}
                          >
                            <Typography color="text.secondary" variant="body2">
                              No CAPCE accreditation on record for this course.
                            </Typography>
                          </Box>
                        )}

                        {/* ANCC */}
                        {activeCourseAnccStatus === 'pending' ? (
                          <Skeleton
                            variant="rectangular"
                            sx={{ height: 80, borderRadius: 1 }}
                          />
                        ) : activeCourseAncc ? (
                          <FieldApproval
                            courseID={activeCourseID}
                            fieldName="Ancc"
                            fieldLabel="ANCC Accreditation"
                            status={activeCourseCompliance?.ancc || 'new'}
                            filter={detailsFilter}
                          >
                            <ANCCApproval accreditation={activeCourseAncc} />
                          </FieldApproval>
                        ) : (
                          <Box
                            sx={{
                              p: 3,
                              bgcolor: 'background.paperDark',
                              borderRadius: 1,
                              textAlign: 'center',
                            }}
                          >
                            <Typography color="text.secondary" variant="body2">
                              No ANCC accreditation on record for this course.
                            </Typography>
                          </Box>
                        )}
                        <FieldApproval
                          courseID={activeCourseID}
                          fieldName="Template"
                          fieldLabel="Template"
                          status={activeCourseCompliance?.template || 'new'}
                          filter={detailsFilter}
                          onEdit={() =>
                            openEditModal('templateID', 'Template', 'select')
                          }
                        >
                          <Typography>
                            {activeCourseTemplateName || 'Not set'}
                          </Typography>
                        </FieldApproval>
                        <FieldApproval
                          courseID={activeCourseID}
                          fieldName="Instructors"
                          fieldLabel="Instructors"
                          status={activeCourseCompliance?.instructors || 'new'}
                          filter={detailsFilter}
                          onEdit={() => setInstructorModalOpen(true)}
                        >
                          {!activeCourseDetails.instructors?.length ? (
                            <Typography>None</Typography>
                          ) : (
                            <Box>
                              {activeCourseDetails.instructors.map(
                                (instructor) => (
                                  <Typography key={instructor.id}>
                                    {'\u2022'} {instructor.name}
                                  </Typography>
                                ),
                              )}
                            </Box>
                          )}
                        </FieldApproval>
                      </Box>
                    )}
                  </Box>

                  {/* Tab 4 — Content */}
                  <Box
                    role="tabpanel"
                    id="tabpanel-4"
                    aria-labelledby="tab-4"
                    hidden={activeTab !== 4}
                  >
                    {activeTab === 4 && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                        }}
                      >
                        {!activeCourseDetailsStatus ||
                        activeCourseDetailsStatus === 'uninitialized' ||
                        activeCourseDetailsStatus === 'pending' ||
                        !activeCourseShowNotesStatus ||
                        activeCourseShowNotesStatus === 'uninitialized' ||
                        activeCourseShowNotesStatus === 'pending' ||
                        !activeCourseVideosStatus ||
                        activeCourseVideosStatus === 'uninitialized' ||
                        activeCourseVideosStatus === 'pending' ? (
                          <Skeleton
                            variant="rectangular"
                            sx={{ height: 80, borderRadius: 1 }}
                          />
                        ) : activeCourseShowNotes.length > 0 ||
                          activeCourseVideos.length > 0 ? (
                          <FieldApproval
                            courseID={activeCourseID}
                            fieldName="Content"
                            fieldLabel="Course Content (Videos & Show Notes)"
                            status={activeCourseCompliance?.content || 'new'}
                            filter={detailsFilter}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                              }}
                            >
                              {orderedCourseContent.map((item) => {
                                let previewLabel =
                                  item.kind === 'video'
                                    ? 'Preview video'
                                    : 'Preview show notes'
                                return (
                                  <Card
                                    key={`${item.kind}-${item.data.id}`}
                                    variant="outlined"
                                    className="flex min-w-0 items-center gap-3 p-4"
                                    sx={{ bgcolor: 'background.paperDark' }}
                                  >
                                    {item.kind === 'video' ? (
                                      <VideoIcon
                                        fontSize="small"
                                        sx={{ color: 'primary.main' }}
                                      />
                                    ) : (
                                      <ShowNotesIcon
                                        fontSize="small"
                                        sx={{ color: 'success.main' }}
                                      />
                                    )}
                                    <Box className="min-w-0 flex-1">
                                      <Typography
                                        variant="body2"
                                        noWrap
                                        sx={{ fontWeight: 'medium' }}
                                      >
                                        {item.data.name}
                                      </Typography>
                                      {item.data.description && (
                                        <Typography
                                          color="text.secondary"
                                          variant="caption"
                                          className="block break-words"
                                        >
                                          {item.data.description}
                                        </Typography>
                                      )}
                                    </Box>
                                    <Tooltip title={previewLabel}>
                                      <IconButton
                                        color="primary"
                                        size="small"
                                        onClick={() =>
                                          setPreviewContentItem(item)
                                        }
                                        aria-label={`${previewLabel}: ${item.data.name}`}
                                      >
                                        <PreviewIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Card>
                                )
                              })}
                            </Box>
                          </FieldApproval>
                        ) : (
                          <Box
                            sx={{
                              p: 3,
                              bgcolor: 'background.paperDark',
                              borderRadius: 1,
                              textAlign: 'center',
                            }}
                          >
                            <Typography color="text.secondary" variant="body2">
                              No content (videos or show notes) on record for
                              this course.
                            </Typography>
                          </Box>
                        )}
                        {previewContentItem?.kind === 'video' && (
                          <VideoPreviewModal
                            isOpen
                            onClose={() => setPreviewContentItem(null)}
                            video={previewContentItem.data}
                          />
                        )}
                        {previewContentItem?.kind === 'showNotes' && (
                          <CourseShowNotesPreviewModal
                            isOpen
                            onClose={() => setPreviewContentItem(null)}
                            showNotesID={previewContentItem.data.id}
                            showNotesName={previewContentItem.data.name}
                            showNotesDescription={
                              previewContentItem.data.description
                            }
                          />
                        )}
                      </Box>
                    )}
                  </Box>

                  {/* Tab 3 — Assessment */}
                  <Box
                    role="tabpanel"
                    id="tabpanel-3"
                    aria-labelledby="tab-3"
                    hidden={activeTab !== 3}
                  >
                    {activeTab === 3 && (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                        }}
                      >
                        {activeCourseQuizStatus === 'pending' ? (
                          <Skeleton
                            variant="rectangular"
                            sx={{ height: 80, borderRadius: 1 }}
                          />
                        ) : activeCourseQuiz ? (
                          <FieldApproval
                            courseID={activeCourseID}
                            fieldName="Quiz"
                            fieldLabel="Final Assessment (Quiz)"
                            status={activeCourseCompliance?.quiz || 'new'}
                            filter={detailsFilter}
                          >
                            <QuizApproval
                              quiz={{
                                ...activeCourseQuiz,
                                questions: activeCourseQuizQuestions || [],
                              }}
                              password=""
                            />
                          </FieldApproval>
                        ) : (
                          <Box
                            sx={{
                              p: 3,
                              bgcolor: 'background.paperDark',
                              borderRadius: 1,
                              textAlign: 'center',
                            }}
                          >
                            <Typography color="text.secondary" variant="body2">
                              No quiz on record for this course.
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                </>
              ) : null}
            </>
          )}
        </Box>
      </Box>
      {/* Edit Modal */}
      <Dialog
        open={editModalOpen}
        onClose={closeEditModal}
        fullWidth
        slotProps={{
          paper: modalPaperProps,
        }}
        maxWidth="md"
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">
              Edit {editModalField?.fieldLabel}
            </Typography>
            <IconButton onClick={closeEditModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {editModalField && activeCourseDetails && (
            <CourseField
              courseID={activeCourseID || ''}
              course={activeCourseDetails}
              fieldName={editModalField.fieldName}
              fieldLabel={editModalField.fieldLabel}
              fieldType={editModalField.fieldType}
              onSave={closeEditModal}
            />
          )}
        </DialogContent>
      </Dialog>
      <Dialog
        open={instructorModalOpen}
        onClose={() => setInstructorModalOpen(false)}
        fullWidth
        slotProps={{
          paper: modalPaperProps,
        }}
        maxWidth="md"
      >
        <DialogTitle>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6">Edit Instructors</Typography>
            <IconButton
              onClick={() => setInstructorModalOpen(false)}
              size="small"
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {activeCourseID && activeCourseDetails && (
            <CourseInstructorsManagement
              courseID={activeCourseID}
              instructors={activeCourseDetails.instructors ?? []}
              complianceStatus={activeCourseCompliance?.instructors}
              complianceFeedback={activeCourseCompliance?.instructorsFeedback}
            />
          )}
        </DialogContent>
      </Dialog>
      <Snackbar
        open={lifecycleOperationStatus === 'fulfilled'}
        autoHideDuration={4000}
        onClose={() => dispatch(clearCourseLifecycleOperationStatus())}
      >
        <Alert
          variant="filled"
          severity="success"
          onClose={() => dispatch(clearCourseLifecycleOperationStatus())}
        >
          {lifecycleOperationSuccess}
        </Alert>
      </Snackbar>
      <Snackbar
        open={lifecycleOperationStatus === 'rejected'}
        autoHideDuration={6000}
        onClose={() => dispatch(clearCourseLifecycleOperationStatus())}
      >
        <Alert
          variant="filled"
          severity="error"
          onClose={() => dispatch(clearCourseLifecycleOperationStatus())}
        >
          {lifecycleOperationError}
        </Alert>
      </Snackbar>
      {activeCourseID && (
        <>
          <ConfirmAction
            open={lifecycleConfirm}
            setOpen={setLifecycleConfirm}
            openText="lifecycle-pre-archive"
            title="Move course to pre-archive?"
            text="The course will be visually distinguished on dashboards but still visible. An email notification will be sent."
            confirmText="Move to pre-archive"
            executeAction={async () => {
              // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
              await dispatch(preArchiveCourse(activeCourseID))
            }}
          />
          <ConfirmAction
            open={lifecycleConfirm}
            setOpen={setLifecycleConfirm}
            openText="lifecycle-reactivate"
            title="Reactivate course?"
            text="The course will return to the active dashboard."
            confirmText="Reactivate"
            executeAction={async () => {
              // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
              await dispatch(reactivateCourse(activeCourseID))
            }}
          />
          <ConfirmAction
            open={lifecycleConfirm}
            setOpen={setLifecycleConfirm}
            openText="lifecycle-archive"
            title="Archive course?"
            text="Archiving will flip CAPCE compliance to Unapproved and hide the course from default views. An email notification will be sent."
            confirmText="Archive"
            executeAction={async () => {
              // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
              await dispatch(archiveCourse(activeCourseID))
            }}
          />
          <ConfirmAction
            open={lifecycleConfirm}
            setOpen={setLifecycleConfirm}
            openText="lifecycle-restore"
            title="Restore course?"
            text="The course will return to pre-archive. CAPCE compliance will remain Unapproved until the course is re-reviewed."
            confirmText="Restore"
            executeAction={async () => {
              // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
              await dispatch(restoreCourse(activeCourseID))
            }}
          />
          <ConfirmAction
            open={lifecycleConfirm}
            setOpen={setLifecycleConfirm}
            openText="delete-course"
            title="Permanently delete course?"
            text={`This will permanently delete "${activeCourseData?.name}" and all associated compliance data. This action cannot be undone. Use Archive for courses that have served their purpose — Delete is only for accidental duplicates that should never have existed.`}
            confirmText="Delete permanently"
            executeAction={async () => {
              // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
              await dispatch(deletePlanningCourseV2(activeCourseID))
              setActiveCourseID(undefined)
            }}
          />
        </>
      )}
    </Box>
  )
}
